import template from './jga-revocation-blocked-message.html.twig';
import './jga-revocation-blocked-message.scss';

const { Component } = Shopware;
const { Criteria } = Shopware.Data;

const FALLBACK_LOCALE = 'en-GB';

Component.register('jga-revocation-blocked-message', {
    template,

    inject: ['repositoryFactory'],

    props: {
        value: {
            type: [Object, String, Array],
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: null,
        },
        helpText: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['update:value'],

    data() {
        return {
            languages: [],
            activeLocale: null,
        };
    },

    created() {
        this.loadLanguages();
    },

    computed: {
        languageRepository() {
            return this.repositoryFactory.create('language');
        },

        translations() {
            // Stored as { locale: html } map. Tolerate legacy string values by
            // putting them under the system default locale.
            if (this.value && typeof this.value === 'object' && !Array.isArray(this.value)) {
                return { ...this.value };
            }
            if (typeof this.value === 'string' && this.value.trim() !== '') {
                return { [this.defaultLocale()]: this.value };
            }
            return {};
        },

        languageOptions() {
            const fromShop = this.languages.map((lang) => ({ value: lang.code, label: lang.name }));
            const seen = new Set(fromShop.map((o) => o.value));
            const extras = [];
            Object.keys(this.translations).forEach((code) => {
                if (!seen.has(code)) {
                    extras.push({ value: code, label: code });
                }
            });
            return [...fromShop, ...extras];
        },

        currentText() {
            return this.translations[this.activeLocale] || '';
        },

        filledLocales() {
            return Object.entries(this.translations)
                .filter(([, value]) => typeof value === 'string' && this.stripHtml(value) !== '')
                .map(([code]) => code);
        },
    },

    methods: {
        async loadLanguages() {
            try {
                const criteria = new Criteria(1, 100);
                criteria.addAssociation('locale');
                criteria.addSorting(Criteria.sort('name', 'ASC'));
                const result = await this.languageRepository.search(criteria, Shopware.Context.api);
                const systemLanguageId = Shopware.Context?.api?.systemLanguageId || null;
                const all = [...result]
                    .map((lang) => ({
                        id: lang.id,
                        name: lang.name,
                        code: lang.locale?.code || null,
                        isDefault: systemLanguageId !== null && lang.id === systemLanguageId,
                    }))
                    .filter((l) => l.code);
                this.languages = [
                    ...all.filter((l) => l.isDefault),
                    ...all.filter((l) => !l.isDefault),
                ];
                if (!this.activeLocale) {
                    this.activeLocale = this.defaultLocale();
                }
            } catch (e) {
                this.languages = [];
                if (!this.activeLocale) {
                    this.activeLocale = FALLBACK_LOCALE;
                }
            }
        },

        defaultLocale() {
            const deLang = this.languages.find((l) => l.code === 'de-DE');
            if (deLang) return 'de-DE';
            const defaultLang = this.languages.find((l) => l.isDefault);
            if (defaultLang?.code) return defaultLang.code;
            if (this.languages.length > 0) return this.languages[0].code;
            return FALLBACK_LOCALE;
        },

        stripHtml(htmlString) {
            if (!htmlString) return '';
            const tmp = document.createElement('div');
            tmp.innerHTML = htmlString;
            return (tmp.textContent || '').trim();
        },

        onTextChange(value) {
            const next = { ...this.translations, [this.activeLocale]: value };
            const cleaned = {};
            Object.entries(next).forEach(([locale, text]) => {
                if (typeof text === 'string' && this.stripHtml(text) !== '') {
                    cleaned[locale] = text;
                }
            });
            // Empty map → emit null so config falls back to default snippets.
            this.$emit('update:value', Object.keys(cleaned).length === 0 ? null : cleaned);
            this.$emit('change', Object.keys(cleaned).length === 0 ? null : cleaned);
        },

        switchLocale(locale) {
            this.activeLocale = locale;
        },
    },
});
